/**
 * Optional photo recognition.
 *
 * Given a downscaled photo, tries to say what is in it — one item or a whole
 * shelf of them — which of our categories each belongs to, and how long each
 * keeps. It never throws and it never blocks a save:
 * the worst case is an outcome the sheet reports in one quiet line, and the user
 * types the details in as they did before this file existed.
 *
 * Failures are reported *with a reason* rather than as a bare null, because the
 * three that actually happen — no key, blocked by the browser, model waffled —
 * need three different sentences to be any use to anyone.
 */

import { readVisionConfig } from "./config";
import { hashImage } from "./imageHash";
import { parseLoosely, toRecognitions } from "./parseRecognition";
import { readCachedRecognition, writeCachedRecognition } from "./recognitionCache";
import type { Recognition } from "./types";

/** Long enough for a cold model, short enough that nobody waits on it. */
const TIMEOUT_MS = 20_000;

export type RecognitionFailure =
  | "unreachable"
  | "not-configured"
  | "no-route"
  | "unauthorised"
  | "no-credits"
  | "model-unavailable"
  | "rate-limited"
  | "server-error"
  | "timeout"
  | "unreadable";

export type RecognitionOutcome =
  | { status: "ok"; results: Recognition[]; cached: boolean }
  | { status: "off" }
  | { status: "failed"; reason: RecognitionFailure };

/**
 * One sentence per failure, written to be useful rather than apologetic.
 *
 * The point of having this many is that they are all *different actions*. When
 * 402, 404 and 500 all rendered as "the photo service had a problem", an
 * account out of credits was indistinguishable from a route that was never
 * deployed, and the only way to tell was to open a browser console on a phone.
 * Each of these now names the thing to go and fix.
 */
export const FAILURE_MESSAGES: Record<RecognitionFailure, string> = {
  unreachable: "Couldn't reach the photo service — add the details below.",
  "not-configured": "No API key on the server — recognition is off until one is set.",
  "no-route": "The recognition endpoint isn't deployed on this site.",
  unauthorised: "The photo service rejected the API key.",
  "no-credits": "The photo service account is out of credits.",
  "model-unavailable":
    "That model isn't available to this key — it may be region-restricted or retired.",
  "rate-limited": "Photo service is rate-limited right now — add the details below.",
  "server-error": "The photo service had a problem — add the details below.",
  timeout: "The photo service took too long — add the details below.",
  unreadable: "Couldn't tell what that is — add the details below.",
};

const PROMPT = `List every distinct food item you can see in this photo.

Reply with JSON only. No prose, no markdown, no code fences. Exactly this shape:
[{"item_name": string, "category": string, "estimated_shelf_life_days": number}]

item_name: 1-3 words, written how someone would write it on a shopping list.
category: exactly one of produce, meat, dairy, pantry, frozen, drinks, other.
estimated_shelf_life_days: whole days it stays good from today, stored the usual way.

List each distinct item once, even if several of it are visible. If there is only
one item, reply with an array of one. Do not invent items you cannot actually
see. If you cannot tell what something is, leave it out rather than guessing a
name you are not confident about.`;

/**
 * What went wrong, from the status **and** the body.
 *
 * The body matters because the status alone is ambiguous, and getting that
 * wrong cost real time: the proxy used to pass an upstream status straight
 * through, so a 404 from NVIDIA — `Function '...': Not found for account`,
 * which is what a region-restricted model returns — was read as our own 404
 * and reported as "the recognition endpoint isn't deployed". It sent us to
 * debug a deployment that was working.
 *
 * Now the proxy answers 502 with `{ code: "upstream", upstreamStatus }` for
 * anything the far end said, and its own refusals carry their own codes. A
 * status with no body is the last honest case: Cloudflare's own 404 when no
 * function is deployed.
 *
 * Exported because the diagnostic in Settings maps the same replies, and two
 * copies of this would drift the first time one was corrected.
 */
export function failureForReply(status: number, body?: unknown): RecognitionFailure {
  const code = (body as { code?: unknown } | undefined)?.code;
  const upstream = (body as { upstreamStatus?: unknown } | undefined)?.upstreamStatus;

  if (code === "no-key") return "not-configured";
  if (code === "our-limit") return "rate-limited";
  if (code === "too-large") return "server-error";
  if (code === "unreachable") return "unreachable";
  if (code === "bad-method") return "no-route";

  if (code === "upstream" && typeof upstream === "number") {
    if (upstream === 401 || upstream === 403) return "unauthorised";
    // "Cloud credits expired" — what the free tier does when the fixed
    // allowance runs out, and the likeliest reason for this working one week
    // and not the next.
    if (upstream === 402) return "no-credits";
    // "Function not found for account": the model is retired, or fenced out of
    // this account's region. Emphatically *not* a deployment problem.
    if (upstream === 404) return "model-unavailable";
    if (upstream === 429) return "rate-limited";
    return "server-error";
  }

  // No code at all: not our handler answering. On Cloudflare a bare 404 or 405
  // means the Pages Function never deployed.
  if (status === 404 || status === 405) return "no-route";
  if (status === 503) return "not-configured";
  if (status === 401 || status === 403) return "unauthorised";
  if (status === 429) return "rate-limited";
  return "server-error";
}

/** Kept for callers that only have a status. */
export function failureForStatus(status: number): RecognitionFailure {
  return failureForReply(status);
}

/**
 * Requests already on the wire, keyed by photo hash.
 *
 * React's StrictMode runs every effect twice in development, so attaching one
 * photo would otherwise cost two API calls — real money against a free-credit
 * allowance, and the cache can't help because neither call has finished when the
 * second starts. Callers asking about the same photo share one request.
 */
const inFlight = new Map<string, Promise<RecognitionOutcome>>();

/**
 * Recognises the food in a photo.
 *
 * Deliberately takes no abort signal. The request is shared, so one caller
 * walking away must not cancel it for another — a caller that has lost interest
 * simply ignores what comes back, and the answer still reaches the cache.
 *
 * @param photo a JPEG data URL from downscalePhoto
 */
export async function recogniseFood(photo: string): Promise<RecognitionOutcome> {
  const config = readVisionConfig();
  if (!config) return { status: "off" };

  // Hashing always succeeds now — there's a pure-JS SHA-256 behind it for the
  // insecure-context case, which is exactly the phone-on-the-LAN case.
  const hash = await hashImage(photo);
  const cached = readCachedRecognition(hash);
  if (cached) return { status: "ok", results: cached, cached: true };

  const existing = inFlight.get(hash);
  if (existing) return existing;

  const request = send(config, photo, hash).finally(() => inFlight.delete(hash));
  inFlight.set(hash, request);
  return request;
}

async function send(
  config: NonNullable<ReturnType<typeof readVisionConfig>>,
  photo: string,
  hash: string
): Promise<RecognitionOutcome> {
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TIMEOUT_MS);

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    // In proxy mode the dev server attaches the real key; the browser never has it.
    if (config.apiKey) headers.Authorization = `Bearer ${config.apiKey}`;

    const response = await fetch(config.endpoint, {
      method: "POST",
      signal: controller.signal,
      headers,
      // In proxy mode the server builds the whole upstream request — model,
      // prompt, token budget — from just the photo. Sending it from here would
      // mean anyone who found the URL could send anything they liked and bill
      // it to whoever owns the key.
      body: JSON.stringify(config.mode === "proxy" ? { photo } : {
        model: config.model,
        // The OpenAI-shaped content parts, which NVIDIA accepts alongside its
        // own inline <img> form. Using the portable one means a proxy or a
        // different provider drops in without touching this file.
        messages: [
          {
            role: "user",
            content: [
              { type: "text", text: PROMPT },
              { type: "image_url", image_url: { url: photo } },
            ],
          },
        ],
        // Low temperature: this is a lookup, not a piece of writing.
        temperature: 0.1,
        top_p: 0.7,
        // A shelf of eight items needs considerably more room than one jar.
        max_tokens: 512,
        stream: false,
      }),
    });

    if (!response.ok) {
      // Read the body: it carries the code that separates our refusals from
      // the upstream's, and the two collide on 404, 429 and 503.
      const problem: unknown = await response.json().catch(() => undefined);
      return { status: "failed", reason: failureForReply(response.status, problem) };
    }

    const body: unknown = await response.json();
    const text = (body as { choices?: { message?: { content?: unknown } }[] })?.choices?.[0]
      ?.message?.content;
    if (typeof text !== "string") return { status: "failed", reason: "unreadable" };

    const results = toRecognitions(parseLoosely(text));
    if (results.length === 0) return { status: "failed", reason: "unreadable" };

    writeCachedRecognition(hash, results);
    return { status: "ok", results, cached: false };
  } catch {
    // A blocked CORS preflight, an offline phone and a dead DNS entry all land
    // here identically — fetch refuses to tell the page which it was.
    return { status: "failed", reason: timedOut ? "timeout" : "unreachable" };
  } finally {
    clearTimeout(timeout);
  }
}
